import { useMutation } from '@tanstack/react-query';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  HStack,
  Link,
  List,
  ListItem,
  Text,
  VStack,
  Wrap,
  WrapItem,
  useToast,
} from '@chakra-ui/react';
import { AIChatApi } from '@/lib/sdk/ai-chat/api';
import { useConfirmAIDraftItem, useRecheckAIDraft } from '@/lib/sdk/ai-chat/queries';
import type { AIDraftIssue, AIDraftStatus, AIDraftView } from '@/lib/sdk/ai-chat/types';

const STATUS: Record<Exclude<AIDraftStatus, ''>, { label: string; color: string }> = {
  ready: { label: 'Ready', color: 'green' },
  needs_confirmation: { label: 'Needs confirmation', color: 'yellow' },
  blocked: { label: 'Blocked', color: 'orange' },
  invalid: { label: 'Being fixed', color: 'red' },
};

// Fields every task has; the others are shown as capability chips
const CORE_FIELDS = new Set(['name', 'description', 'type', 'actions', 'mutable', 'data']);

interface DraftCardProps {
  sessionId: string;
  view: AIDraftView;
  onOpenInEditor: () => void;
  isOpening: boolean;
}

/**
 * The draft so far: its tasks with the capabilities configured on each, then what
 * stops it from opening in the editor: blockers the user resolves (with a link to the
 * page and, for a dataset, its CSV template), items to confirm, and the assistant's
 * assumptions.
 */
export function DraftCard({ sessionId, view, onOpenInEditor, isOpening }: DraftCardProps) {
  const toast = useToast();
  const recheck = useRecheckAIDraft();
  const confirm = useConfirmAIDraftItem();
  const template = useMutation({
    mutationFn: (dataset: string) => AIChatApi.getDatasetTemplate(sessionId, dataset),
    onSuccess: (blob, dataset) => {
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${dataset}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    },
    onError: (error) => toast({ status: 'error', title: 'The template could not be downloaded', description: error.message }),
  });

  if (!view.draft) return null;
  const status = view.status ? STATUS[view.status] : null;
  const blockers = view.issues.filter((issue) => issue.severity === 'blocker');
  const confirms = view.issues.filter((issue) => issue.severity === 'confirm');
  const errors = view.issues.filter((issue) => issue.severity === 'error');

  return (
    <VStack align="stretch" spacing={3} borderWidth="1px" borderColor="border-default" borderRadius="md" p={3} data-testid="ai-build-draft-card">
      <HStack justify="space-between">
        <Text fontWeight="semibold" noOfLines={1}>
          {view.draft.workflow.title || 'Untitled workflow'}
        </Text>
        {status && (
          <Badge colorScheme={status.color} data-testid="ai-build-draft-status" data-status={view.status}>
            {status.label}
          </Badge>
        )}
      </HStack>

      <List spacing={1} fontSize="sm">
        {view.draft.tasks.map((task) => (
          <ListItem key={task.key} data-testid={`ai-build-draft-task-${task.key}`}>
            <Text as="span" fontWeight="medium">
              {String(task.fields.name ?? task.key)}
            </Text>{' '}
            <Text as="span" color="text-secondary">
              · {String(task.fields.type ?? '')}
            </Text>
            <Wrap spacing={1} mt={0.5}>
              {Object.keys(task.fields)
                .filter((field) => !CORE_FIELDS.has(field) && task.fields[field] != null)
                .map((field) => (
                  <WrapItem key={field}>
                    <Badge variant="subtle" fontSize="2xs">
                      {field.replace(/_/g, ' ')}
                    </Badge>
                  </WrapItem>
                ))}
            </Wrap>
          </ListItem>
        ))}
      </List>

      {errors.length > 0 && (
        <Text fontSize="xs" color="text-secondary">
          The assistant is still fixing {errors.length} problem{errors.length === 1 ? '' : 's'}.
        </Text>
      )}

      {blockers.length > 0 && (
        <Box>
          <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color="text-secondary" mb={1}>
            Create first
          </Text>
          <VStack align="stretch" spacing={2}>
            {blockers.map((issue, index) => (
              <BlockerItem key={`${issue.code}-${index}`} issue={issue} onTemplate={(dataset) => template.mutate(dataset)} />
            ))}
          </VStack>
        </Box>
      )}

      {confirms.length > 0 && (
        <Box>
          <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color="text-secondary" mb={1}>
            Confirm
          </Text>
          <VStack align="stretch" spacing={1}>
            {confirms.map((issue) => (
              <Checkbox
                key={issue.confirm_id}
                size="sm"
                isChecked={!!issue.confirmed}
                isDisabled={confirm.isPending}
                onChange={(event) =>
                  confirm.mutate({ sessionId, itemId: issue.confirm_id ?? '', confirmed: event.target.checked })
                }
                data-testid={`ai-build-confirm-${issue.confirm_id}`}
              >
                <Text fontSize="sm">{issue.message}</Text>
              </Checkbox>
            ))}
          </VStack>
        </Box>
      )}

      {(view.assumptions?.length ?? 0) > 0 && (
        <Box>
          <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color="text-secondary" mb={1}>
            Assumed
          </Text>
          <List fontSize="sm" styleType="disc" pl={4}>
            {view.assumptions?.map((assumption) => <ListItem key={assumption}>{assumption}</ListItem>)}
          </List>
        </Box>
      )}

      <HStack>
        <Button size="sm" variant="outline" onClick={() => recheck.mutate(sessionId)} isLoading={recheck.isPending} data-testid="ai-build-recheck-btn">
          Re-check
        </Button>
        <Button
          size="sm"
          colorScheme="brand"
          onClick={onOpenInEditor}
          isLoading={isOpening}
          isDisabled={view.status !== 'ready'}
          data-testid="ai-build-open-btn"
        >
          Open in editor
        </Button>
      </HStack>
    </VStack>
  );
}

function BlockerItem({ issue, onTemplate }: { issue: AIDraftIssue; onTemplate: (dataset: string) => void }) {
  return (
    <Box fontSize="sm" data-testid={`ai-build-blocker-${issue.code}`}>
      <Text>{issue.message}</Text>
      <HStack spacing={3} mt={1}>
        {issue.link && (
          <Link href={issue.link} isExternal color="brand.500" fontWeight="medium" data-testid="ai-build-blocker-fix">
            Fix
          </Link>
        )}
        {issue.dataset && (issue.columns?.length ?? 0) > 0 && (
          <Button size="xs" variant="link" onClick={() => onTemplate(issue.dataset ?? '')} data-testid="ai-build-blocker-template">
            CSV template
          </Button>
        )}
      </HStack>
    </Box>
  );
}
